/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

let didLogThemeFallback = false;
let didLogBoxShadowFallback = false;

export default styled(Box)(({ theme = {}, ownerState }) => {
  const {
    palette = {},
    functions: themeFunctions = {},
    borders: themeBorders = {},
    boxShadows: themeBoxShadows = {},
  } = theme || {};
  const { variant, bgColor, color, opacity, borderRadius, shadow } = ownerState;

  const { gradients = {}, grey = {}, white = {} } = palette;
  const { linearGradient } = themeFunctions || {};
  const radius = themeBorders.borderRadius || {};
  const themeHasBorderRadius = Boolean(themeBorders && themeBorders.borderRadius);
  const themeHasBoxShadows = Boolean(themeBoxShadows && Object.keys(themeBoxShadows).length);

  const debugContext = () => ({
    route: typeof window !== "undefined" ? window.location?.pathname : "server",
    ownerState,
    themeKeys: Object.keys(theme || {}),
    themeBordersKeys: Object.keys(themeBorders || {}),
  });

  if (!themeHasBorderRadius && !didLogThemeFallback) {
    // eslint-disable-next-line no-console
    console.error("[VuiBox] theme.borders.borderRadius ausente. Aplicando fallback.", debugContext());
    didLogThemeFallback = true;
  }

  if (!themeHasBoxShadows && !didLogBoxShadowFallback) {
    // eslint-disable-next-line no-console
    console.error("[VuiBox] theme.boxShadows ausente. Aplicando fallback.", debugContext());
    didLogBoxShadowFallback = true;
  }

  const greyColors = {
    "grey-100": grey[100],
    "grey-200": grey[200],
    "grey-300": grey[300],
    "grey-400": grey[400],
    "grey-500": grey[500],
    "grey-600": grey[600],
    "grey-700": grey[700],
    "grey-800": grey[800],
    "grey-900": grey[900],
  };

  const validGradients = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ];

  const validColors = [
    "transparent",
    "white",
    "black",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "text",
    "grey-100",
    "grey-200",
    "grey-300",
    "grey-400",
    "grey-500",
    "grey-600",
    "grey-700",
    "grey-800",
    "grey-900",
  ];

  const validBorderRadius = ["xs", "sm", "md", "lg", "xl", "xxl", "section"];
  const validBoxShadows = ["xs", "sm", "md", "lg", "xl", "xxl", "inset"];

  // background value
  let backgroundValue = bgColor;

  if (variant === "gradient") {
    backgroundValue = validGradients.find((el) => el === bgColor)
      ? linearGradient(gradients[bgColor].main, gradients[bgColor].state)
      : white.main;
  } else if (validColors.find((el) => el === bgColor)) {
    backgroundValue = palette[bgColor] ? palette[bgColor].main : greyColors[bgColor];
  } else {
    backgroundValue = bgColor;
  }

  // color value
  let colorValue = color;

  if (validColors.find((el) => el === color)) {
    colorValue = palette[color] ? palette[color].main : greyColors[color];
  }

  // borderRadius value
  let borderRadiusValue = borderRadius;

  if (validBorderRadius.find((el) => el === borderRadius)) {
    borderRadiusValue = radius[borderRadius];
  }

  if (!borderRadiusValue && typeof borderRadius === "number") {
    borderRadiusValue = borderRadius;
  }

  // boxShadow value
  let boxShadowValue = themeBoxShadows;

  if (validBoxShadows.find((el) => el === shadow) && themeBoxShadows[shadow]) {
    boxShadowValue = themeBoxShadows[shadow];
  }

  return {
    opacity,
    background: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
  };
});
